import React from 'react';
import { Badge, Button, Column, Modal, Select } from '@odin-labs/components';
import {
  AccessGroupSyncState,
  GetAccessGroupDetailsQuery,
  useRetryAccessGroupPushMutation,
  GetSiteAccessGroupsQuery,
  useGetAccessGroupDetailsQuery,
  useAddZoneToAccessGroupMutation,
  useRemoveZoneFromAccessGroupMutation,
  useSetDefaultAccessGroupMutation,
  useArchiveAccessGroupMutation,
} from 'apollo/generated/client-operations';
import { LoadingError } from 'components/loadingError';
import {
  actorName,
  formatAccessDate,
  historyDescription,
  zoneStateLabels,
} from './presentation';
import { AccessGroupEditor } from './AccessGroupEditor';
import {
  AccessGroupsError,
  AccessGroupActionFeedback,
  ProviderDetails,
} from './AccessGroupStatus';
import { AccessGroupsTable } from './AccessGroupsTable';
import { AccessGroupMembers } from './AccessGroupMembers';
import { useAccessGroupAction } from './useAccessGroupAction';

type Group = NonNullable<GetAccessGroupDetailsQuery['accessGroup']>;
type Confirmation =
  | { kind: 'retry' }
  | { kind: 'default' }
  | { kind: 'archive' }
  | { kind: 'removeZone'; zoneId: string; grantId: string; name: string };
function confirmationCopy(
  confirmation: Confirmation,
  name: string,
): { title: string; description: string } {
  switch (confirmation.kind) {
    case 'retry':
      return {
        title: 'Retry group push?',
        description:
          'This retries group configuration for this site’s Avigilon connector. Durable synchronization and worker hardware access are verified separately.',
      };
    case 'archive':
      return {
        title: `Archive ${name}?`,
        description:
          'This permanently archives the group and closes all active memberships and zone grants. Provider cleanup is reconciled separately.',
      };
    case 'default':
      return {
        title: `Make ${name} the default?`,
        description:
          'The current default will be replaced. Existing memberships are not moved.',
      };
    case 'removeZone':
      return {
        title: `Remove ${confirmation.name}?`,
        description:
          'This removes the zone grant. History is retained and provider changes are reconciled separately.',
      };
    default: {
      const exhaustive: never = confirmation;
      return exhaustive;
    }
  }
}
const getHistoryColumns = (
  timeZone: string,
): Column<Group['history'][number]>[] => [
  { id: 'change', Header: 'Change', accessor: historyDescription },
  { id: 'actor', Header: 'By', accessor: (entry) => actorName(entry.actor) },
  {
    id: 'date',
    Header: 'When',
    accessor: (entry) => formatAccessDate(entry.occurredAt, timeZone),
  },
];

export function AccessGroupDetails({
  siteId,
  groupId,
  timeZone,
  zones,
  refreshList,
  onClose,
  onSaved,
}: {
  siteId: string;
  groupId: string;
  timeZone: string;
  zones: GetSiteAccessGroupsQuery['siteConfigurationZones'];
  onSaved?: (message: string) => void;
  refreshList: () => Promise<unknown>;
  onClose: () => void;
}): React.ReactElement {
  const { data, loading, error, refetch } = useGetAccessGroupDetailsQuery({
    variables: { siteId, id: groupId },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });
  const group = data?.accessGroup;
  // Mounted closed and opened at once, so the drawer slides in as the other
  // drawers do rather than appearing in place; closing slides it out before
  // onClose unmounts it.
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  React.useEffect(() => setDrawerOpen(true), []);
  const historyColumns = React.useMemo(
    () => getHistoryColumns(timeZone),
    [timeZone],
  );
  const [renaming, setRenaming] = React.useState(false);
  const [zoneId, setZoneId] = React.useState<string | null>(null);
  const [confirmation, setConfirmation] = React.useState<Confirmation | null>(
    null,
  );
  const [retryPush] = useRetryAccessGroupPushMutation();
  const [addZone] = useAddZoneToAccessGroupMutation();
  const [removeZone] = useRemoveZoneFromAccessGroupMutation();
  const [setDefault] = useSetDefaultAccessGroupMutation();
  const [archive] = useArchiveAccessGroupMutation();
  // Bumped only by the actions that change membership, so member pages reload
  // after archiving but not after zone, name or default changes.
  const [membersVersion, setMembersVersion] = React.useState(0);
  const refresh = (): Promise<unknown> =>
    Promise.all([refetch(), refreshList()]);
  const action = useAccessGroupAction(refresh, onSaved);
  const input = { siteId, accessGroupId: groupId };
  const availableZones = zones
    .filter((zone) => !group?.zones.some((grant) => grant.zone.id === zone.id))
    .map((zone) => ({
      label: `${zone.name} (${zoneStateLabels[zone.state]})`,
      value: zone.id,
    }));
  const confirmationStillApplies =
    group &&
    confirmation &&
    (confirmation.kind === 'retry' ||
      (!group.archivedAt &&
        (confirmation.kind === 'removeZone'
          ? group.zones.some((grant) => grant.id === confirmation.grantId)
          : !group.isDefault)));
  React.useEffect(() => {
    if (confirmation && !loading && !error && !confirmationStillApplies)
      setConfirmation(null);
  }, [confirmation, confirmationStillApplies, loading, error]);
  const confirm = async (): Promise<void> => {
    if (
      !confirmation ||
      !confirmationStillApplies ||
      action.disabled ||
      loading ||
      error
    )
      return;
    const execute = (): Promise<unknown> => {
      switch (confirmation.kind) {
        case 'retry':
          return retryPush({ variables: { input } });
        case 'archive':
          return archive({ variables: { input } });
        case 'default':
          return setDefault({ variables: { input } });
        case 'removeZone':
          return removeZone({
            variables: { input: { ...input, zoneId: confirmation.zoneId } },
          });
        default: {
          const exhaustive: never = confirmation;
          return exhaustive;
        }
      }
    };
    if (
      await action.execute(
        execute,
        confirmation.kind === 'retry'
          ? 'Group push queued. The status updates when synchronization finishes.'
          : 'Configuration saved in Odin.',
      )
    ) {
      // Archiving closes every membership; other actions leave members as is.
      if (confirmation.kind === 'archive') setMembersVersion((v) => v + 1);
      setConfirmation(null);
    }
  };
  const canEdit =
    !loading && !error && group && !group.archivedAt && !action.disabled;
  return (
    <>
      <Modal
        open={drawerOpen}
        setOpen={(open) => {
          if (!open && !action.busy) setDrawerOpen(false);
        }}
        afterLeave={onClose}
        variant="drawer"
        size="lg"
        title={group?.name ?? 'Access group'}
        actionsPanel={null}
      >
        <AccessGroupActionFeedback action={action} />
        {loading && !data && (
          <div role="status" aria-label="Loading access group">
            <LoadingError loading />
          </div>
        )}
        {error && (
          <AccessGroupsError
            retry={() => {
              void action.retryRefresh();
            }}
          />
        )}
        {!loading && !error && !group && (
          <p>Access group not found at this site.</p>
        )}
        {!error && group && (
          <div className="odin-space-y-8" aria-busy={loading}>
            {loading && (
              <p
                role="status"
                aria-label="Refreshing access group"
                className="odin-text-sm odin-text-gray-500"
              >
                Refreshing access group…
              </p>
            )}
            <div className="odin-flex odin-flex-wrap odin-gap-2">
              {group.isDefault && <Badge text="Default" color="blue" />}
              {group.archivedAt && <Badge text="Archived" color="gray" />}
              {!group.archivedAt && (
                <>
                  <Button
                    text="Rename"
                    theme="secondary"
                    size="sm"
                    disabled={!canEdit}
                    onClick={() => setRenaming(true)}
                  />
                  <Button
                    text="Set as default"
                    theme="secondary"
                    size="sm"
                    disabled={!canEdit || group.isDefault}
                    onClick={() => setConfirmation({ kind: 'default' })}
                  />
                  <Button
                    text="Archive group"
                    theme="danger"
                    size="sm"
                    disabled={!canEdit || group.isDefault}
                    onClick={() => setConfirmation({ kind: 'archive' })}
                  />
                </>
              )}
            </div>
            {group.isDefault && (
              <p className="odin-text-sm odin-text-gray-600">
                Replace the default before archiving this group. Default
                membership can be revoked from individual workers.
              </p>
            )}
            {group.archivedAt && (
              <p className="odin-text-sm">
                Archived {formatAccessDate(group.archivedAt, timeZone)} by{' '}
                {actorName(group.archivedBy)}. Archived groups cannot be
                restored.
              </p>
            )}
            <ProviderDetails group={group} timeZone={timeZone} />
            {(group.avigilon.state === AccessGroupSyncState.Failed ||
              group.avigilon.state === AccessGroupSyncState.Pending) && (
              <Button
                text="Retry group push"
                theme="secondary"
                disabled={action.disabled || loading}
                onClick={() => setConfirmation({ kind: 'retry' })}
              />
            )}
            <section className="odin-space-y-4" aria-label="Zone grants">
              <h3 className="odin-font-semibold">Zone grants</h3>
              {!group.zones.length && (
                <p className="odin-text-sm odin-text-gray-500">
                  No zones granted. Add a zone to define this group’s intended
                  access.
                </p>
              )}
              <ul className="odin-divide-y odin-divide-gray-200">
                {group.zones.map((grant) => (
                  <li
                    key={grant.id}
                    className="odin-flex odin-items-center odin-justify-between odin-gap-4 odin-py-3"
                  >
                    <div>
                      <p>
                        {grant.name}{' '}
                        <Badge
                          text={zoneStateLabels[grant.state]}
                          color="gray"
                        />
                      </p>
                      <p className="odin-text-xs odin-text-gray-500">
                        Granted {formatAccessDate(grant.grantedAt, timeZone)} by{' '}
                        {actorName(grant.grantedBy)}
                      </p>
                    </div>
                    {!group.archivedAt && (
                      <Button
                        text={`Remove ${grant.name}`}
                        theme="naked"
                        size="sm"
                        disabled={!canEdit}
                        onClick={() =>
                          setConfirmation({
                            kind: 'removeZone',
                            zoneId: grant.zone.id,
                            grantId: grant.id,
                            name: grant.name,
                          })
                        }
                      />
                    )}
                  </li>
                ))}
              </ul>
              {!group.archivedAt && (
                <div className="odin-flex odin-items-end odin-gap-3">
                  <Select
                    className="odin-flex-1"
                    name="grant-zone"
                    instanceId="grant-zone"
                    inputId="grant-zone"
                    label="Zone to grant"
                    options={availableZones}
                    value={
                      availableZones.find((zone) => zone.value === zoneId) ??
                      null
                    }
                    onChange={(option) => setZoneId(option?.value ?? null)}
                    isDisabled={!canEdit}
                  />
                  <Button
                    text="Add zone"
                    disabled={
                      !canEdit ||
                      !availableZones.some((zone) => zone.value === zoneId)
                    }
                    withSpinner={action.busy}
                    onClick={async () => {
                      if (
                        !canEdit ||
                        !zoneId ||
                        !availableZones.some((zone) => zone.value === zoneId)
                      )
                        return;
                      if (
                        await action.execute(
                          () =>
                            addZone({
                              variables: { input: { ...input, zoneId } },
                            }),
                          'Zone grant saved in Odin.',
                        )
                      )
                        setZoneId(null);
                    }}
                  />
                </div>
              )}
            </section>
            <section className="odin-space-y-4" aria-label="Group members">
              <h3 className="odin-font-semibold">
                Members ({group.memberCount})
              </h3>
              <p className="odin-text-sm odin-text-gray-500">
                Manage memberships from a worker’s profile.
              </p>
              <AccessGroupMembers
                siteId={siteId}
                groupId={groupId}
                memberCount={group.memberCount}
                timeZone={timeZone}
                version={membersVersion}
              />
            </section>
            <section className="odin-space-y-4" aria-label="Group history">
              <h3 className="odin-font-semibold">History</h3>
              <AccessGroupsTable
                enablePagination
                columns={historyColumns}
                data={group.history}
                noResultsText="No history recorded"
                disableGlobalFilter
              />
            </section>
          </div>
        )}
      </Modal>
      {renaming && group && (
        <AccessGroupEditor
          siteId={siteId}
          group={group}
          refresh={refresh}
          onSaved={onSaved}
          onClose={() => setRenaming(false)}
        />
      )}
      {confirmation && (
        <Modal
          open
          title={confirmationCopy(confirmation, group?.name ?? 'group').title}
          size="sm"
          setOpen={(open) => {
            if (!open && !action.busy) setConfirmation(null);
          }}
          actionText={
            confirmation.kind === 'archive'
              ? 'Confirm archive'
              : 'Confirm change'
          }
          actionButtonTheme={
            confirmation.kind === 'archive' ||
            confirmation.kind === 'removeZone'
              ? 'danger'
              : 'primary'
          }
          onAction={confirm}
          actionButtonWithSpinner={action.busy}
          actionButtonEnabled={
            !action.disabled &&
            !loading &&
            !error &&
            Boolean(confirmationStillApplies)
          }
          cancelButtonEnabled={!action.busy}
        >
          <p className="odin-mb-4 odin-text-sm">
            {confirmationCopy(confirmation, group?.name ?? 'group').description}
          </p>
          <AccessGroupActionFeedback action={action} />
        </Modal>
      )}
    </>
  );
}
